import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import PageLayout from '~layouts/page/pageLayout';

import ErrorType from './errorType';
import ErrorView from './errorView';

interface ErrorPageProps {
	type: ErrorType;
	/** Optional override for the description text. */
	description?: string;
	/** Callback for the action button. When omitted, no button is rendered. */
	onAction?: () => void;
}

/**
 * Full-page error wrapper.
 *
 * Renders an ErrorView inside a PageLayout so it gets the standard
 * app chrome (sidebar, header). Use this for route-level errors.
 *
 * For inline/embedded error states (e.g. inside a detail page that
 * already has its own PageLayout), use ErrorView directly.
 */
const ErrorPage = ({ type, description, onAction }: ErrorPageProps) => {
	const { t } = useTranslation();
	const title = pageTitleByType(type, t);

	return (
		<PageLayout pageTitle={title} hideBreadcrumbs>
			<ErrorView type={type} description={description} onAction={onAction} />
		</PageLayout>
	);
};

function pageTitleByType(type: ErrorType, t: TFunction): string {
	switch (type) {
		case ErrorType.NotFound:
			return t('notFound', { ns: 'status' });
		case ErrorType.NotAuthorized:
			return t('unauthorized', { ns: 'status' });
		case ErrorType.Forbidden:
			return t('forbidden', { ns: 'status' });
		case ErrorType.Other:
			return t('somethingWentWrong', { ns: 'errors' });
		default:
			throw new Error(`Unhandled ErrorType: ${type}`);
	}
}

export default ErrorPage;
